import React, { useState, useEffect, useRef } from 'react'
import { X, FileText, Upload, CheckCircle, XCircle, Download, User, MapPin, Globe, Building, Calendar, Globe as IpIcon, CheckSquare } from 'lucide-react'
import html2pdf from 'html2pdf.js'

const AgreementPopup = ({
  isOpen,
  onClose,
  profile,
  onAgreementSubmit,
  userData
}) => {
  const [formData, setFormData] = useState({
    contactPerson: '',
    city: '',
    country: '',
    companyAddress: '',
    companyName: '',
    registrationNumber: ''
  })

  const [signatureOption, setSignatureOption] = useState('upload')
  const [uploadedSignature, setUploadedSignature] = useState(null)
  const [uploadedSignatureUrl, setUploadedSignatureUrl] = useState(null)
  const [generatedSignature, setGeneratedSignature] = useState('')
  const [selectedSignatureStyle, setSelectedSignatureStyle] = useState('')
  const [agreementChecks, setAgreementChecks] = useState({
    termsAccepted: false,
    privacyAccepted: false
  })

  const [ipAddress, setIpAddress] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const [isAgreementAccepted, setIsAgreementAccepted] = useState(false)
  const [acceptedDetails, setAcceptedDetails] = useState({
    city: '',
    dateOfAcceptance: '',
    ipAddress: '',
    signatureImage: '',
    signatureFontStyle: '',
    agreementAccepted: false
  })
  const [formErrors, setFormErrors] = useState({
    city: ''
  })

  // Validate city input
  const validateCity = (city) => {
    if (!city || city.trim() === '') {
      return 'City is required';
    }

    // Remove leading/trailing spaces for validation
    const trimmedCity = city.trim();

    // Check if city contains only letters, spaces, hyphens, and apostrophes
    // This allows for city names like "New York", "St. Louis", "San Francisco"
    const validCityRegex = /^[A-Za-zÀ-ÿ\s\-'.]+$/;

    if (!validCityRegex.test(trimmedCity)) {
      return 'City should contain only letters, spaces, hyphens (-), apostrophes (\') and periods (.)';
    }

    // Check if it's just spaces or special characters
    const lettersOnly = trimmedCity.replace(/[\s\-'.]/g, '');
    if (lettersOnly.length === 0) {
      return 'City must contain at least one letter';
    }

    // Check minimum length (at least 2 letters)
    if (lettersOnly.length < 2) {
      return 'City name is too short';
    }

    // Check maximum length
    if (trimmedCity.length > 100) {
      return 'City name is too long (max 100 characters)';
    }

    return '';
  };

  const agreementRef = useRef(null)

  // Helper function to extract font name from style
  const extractFontName = (styleString) => {
    if (!styleString) return '';

    // Pattern 1: font-['Font_Name']
    const match1 = styleString.match(/font-\[['"]([^'"]+)['"]\]/);
    if (match1 && match1[1]) return match1[1];

    // Pattern 2: font-family: 'Font_Name'
    const match2 = styleString.match(/font-family:\s*['"]([^'"]+)['"]/);
    if (match2 && match2[1]) return match2[1];

    // Pattern 3: Any quoted string
    const match3 = styleString.match(/['"]([^'"]+)['"]/);
    if (match3 && match3[1]) return match3[1];

    // Pattern 4: Clean CSS classes
    const cleaned = styleString
      .replace(/font-\[/g, '')
      .replace(/\]/g, '')
      .replace(/['"]/g, '')
      .replace(/text-\w+/g, '')
      .replace(/text-center/g, '')
      .replace(/py-\d+/g, '')
      .replace(/dark:text-\w+/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    return cleaned || styleString;
  };

  // All 8 signature styles from AcceptingAgreement component
  const signatureStyles = [
    {
      id: 1,
      name: 'Elegant Script',
      template: (name) => {
        const parts = name.split(' ')
        if (parts.length > 1) {
          return `${parts[0]}\n${parts.slice(1).join(' ')}`
        }
        return name
      },
      style: 'italic text-2xl text-gray-700 dark:text-gray-900 font-poppinsRegular text-center leading-tight'
    },
    {
      id: 2,
      name: 'Corporate Stamp',
      template: (name) => name,
      style: 'border-2 border-gray-500 px-4 py-3 rounded-lg bg-white font-mono text-lg font-bold text-center shadow-sm'
    },
    {
      id: 3,
      name: 'Calligraphic',
      template: (name) => name,
      style: 'text-2xl italic text-gray-700 dark:text-gray-900 font-poppinsRegular text-center'
    },
    {
      id: 4,
      name: 'Autograph Flow',
      template: (name) => name,
      style: "font-['Great_Vibes'] text-4xl text-blue-900 dark:text-blue-300 text-center py-2"
    },
    {
      id: 5,
      name: 'Marker Script',
      template: (name) => name,
      style: "font-['Dancing_Script'] text-3xl font-bold text-indigo-800 dark:text-indigo-400 text-center"
    },
    {
      id: 6,
      name: 'Vintage Quill',
      template: (name) => name,
      style: "font-['Herr_Von_Muellerhoff'] text-4xl text-gray-600 dark:text-gray-400 text-center leading-none"
    },
    {
      id: 7,
      name: 'Artistic Brush',
      template: (name) => name,
      style: "font-['Alex_Brush'] text-3xl text-gray-900 text-center tracking-wide"
    },
    {
      id: 8,
      name: 'Casual Note',
      template: (name) => name.toLowerCase(),
      style: "font-['Sacramento'] text-4xl text-gray-900 text-center"
    }
  ]

  useEffect(() => {
    if (isOpen) {
      fetchIpAddress()
      initializeFormData()
      checkExistingAgreement()
    }

    // Load Google Fonts for signature styles
    loadGoogleFonts()
  }, [isOpen, profile])

  // Check if agreement is already accepted
  const checkExistingAgreement = () => {
    if (profile?.agreementAccepted) {
      setIsAgreementAccepted(true)
      setAcceptedDetails({
        city: profile.city || '',
        dateOfAcceptance: profile.dateOfAcceptance || '',
        ipAddress: profile.ipAddress || '',
        signatureImage: profile.signatureImage || '',
        signatureFontStyle: profile.signatureFontStyle || '',
        agreementAccepted: profile.agreementAccepted || false
      })
    }
  }

  // Function to load Google Fonts for signature styles
  const loadGoogleFonts = () => {
    const fonts = [
      'Great+Vibes',
      'Dancing+Script',
      'Herr+Von+Muellerhoff',
      'Alex+Brush',
      'Sacramento'
    ]

    fonts.forEach(font => {
      const link = document.createElement('link')
      link.href = `https://fonts.googleapis.com/css2?family=${font}&display=swap`
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    })
  }

  const initializeFormData = () => {
    setFormData({
      contactPerson: profile?.name || '',
      city: profile?.city || '',
      country: profile?.country || '',
      companyAddress: profile?.businessAddress || '',
      companyName: profile?.legalBusinessName || '',
      registrationNumber: profile?.businessRegistrationNumber || ''
    })
  }

  const fetchIpAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      setIpAddress(data.ip)
    } catch (error) {
      console.error('Error fetching IP address:', error)
      setIpAddress('Unknown')
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Validate city input in real-time
    if (name === 'city') {
      const error = validateCity(value);
      setFormErrors(prev => ({ ...prev, city: error }));
    }
  }
  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml']
      const maxSize = 10 * 1024 * 1024

      if (!validTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Please upload a valid image file (JPEG, PNG, SVG)' })
        return
      }

      if (file.size > maxSize) {
        setMessage({ type: 'error', text: 'File size must be less than 10MB' })
        return
      }

      setUploadedSignature(file)
      const url = URL.createObjectURL(file)
      setUploadedSignatureUrl(url)
      setMessage({ type: 'success', text: 'Signature uploaded successfully' })
    }
  }

  const handleGenerateSignature = (style) => {
    const nameToUse = formData.contactPerson || formData.companyName || 'Your Name'
    if (nameToUse && nameToUse !== 'Your Name') {
      const signature = style.template(nameToUse)
      setGeneratedSignature(signature)
      setSelectedSignatureStyle(style.name)
      setMessage({ type: 'success', text: 'Signature generated successfully' })
    } else {
      setMessage({ type: 'error', text: 'Please enter your name in the required details section first' })
    }
  }

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setAgreementChecks(prev => ({
      ...prev,
      [name]: checked
    }))
  }

  const getUpdatedAgreement = () => {
    const highlightText = (text, placeholder, userInput) => {
      if (!userInput) return text
      const regex = new RegExp(placeholder, 'g')
      return text.replace(regex, `**${userInput}**`)
    }

    let agreement = `**AXONALL TRAVEL AGENT AGREEMENT**

This Travel Agent Agreement ("Agreement") is entered into between Axonall Ltd, a company registered in England under company number 15851539, with its principal place of business at 2nd Floor College House, 17 King Edwards Road, Ruislip, London, United Kingdom, HA4 7AE ("Axonall"), and ${formData.companyName || '[Your Company]'}, a company registered under number ${formData.registrationNumber || '[Registration Number]'} with business address at ${formData.companyAddress || '[Your Address]'} (the "Agent"). Axonall and the Agent are together the "Parties" and each a "Party".

By clicking "I Accept" or otherwise electronically accepting this Agreement via the Axonall Travel Agent Extranet (the "Extranet"), the Agent confirms that it has read, understood, and agrees to be legally bound by the terms and conditions set forth in this Agreement.

This Agreement becomes effective on the date the Agent's account is activated by Axonall (the "Effective Date").

This Agreement governs the Agent's use of the Extranet and its ability to make bookings of travel-related products and services offered by third-party providers ("Suppliers") via the Axonall platform for the Agent's own end customers ("Clients").

The Agent acknowledges and agrees that it has received, read, and accepted the Axonall General User Terms (the "General Terms") as published in the Axonall portal, which are incorporated by reference and form part of this Agreement. Capitalised terms not defined in this Agreement have the meaning given to them in the General Terms. In the event of any conflict between this Agreement and the General Terms, this Agreement shall prevail in relation to the Agent's role as a travel agent or reseller.

**1. PURPOSE AND SCOPE**
**1.1 This Agreement governs:**
a. the Agent's access to and use of the Extranet; and
b. the Agent's making of Bookings for travel-related products and services offered by Suppliers via the Axonall platform, on behalf of the Agent's own Clients.

**1.2 The Parties acknowledge that:**
a. Axonall operates a curated digital platform through which travel-related products and services are made available for Booking; and
b. Suppliers remain solely responsible for the performance of the underlying travel products and services provided to Clients.

**2. ROLE OF THE PARTIES**
**2.1 Axonall facilitates access to curated travel experiences, packages, accommodations and related services via its digital platform and Extranet. Axonall does not act as the Agent of the Client and does not provide the underlying travel services.**

**2.2 The Agent is an independent entity and:**
a. acts as the Client's travel advisor or reseller;
b. remains fully responsible for its relationship with its Clients, including communications, advice, and ongoing support; and
c. is solely responsible for complying with all Applicable Laws that apply to travel agents, tour operators or resellers in the territories in which it operates.

**2.3 Nothing in this Agreement or the General Terms creates an employment, partnership, or joint venture relationship between Axonall and the Agent. The Agent has no authority to bind Axonall to any contract with any Client.**

**3. COMMISSION STRUCTURE AND PAYMENT**

**3.1 Commission Eligibility**
a. Axonall shall pay the Agent commission for each eligible Booking made by the Agent via the Extranet that becomes a Materialized Booking, in accordance with the General Terms.
b. The applicable commission rates, eligibility rules and calculation methods shall be displayed in the Agent's dashboard within the Extranet or otherwise notified by Axonall from time to time.

**3.2 Changes to Commission**
Axonall may amend commission tiers, rates or eligibility criteria from time to time. Any such changes shall be notified via the Extranet or by email and shall apply to Bookings made after the effective date of the change.

**3.3 Materialized Bookings**
Commission is only earned on Materialized Bookings and only after Axonall has received all relevant amounts or commission from the applicable Supplier. No commission is payable on Bookings which are cancelled, refunded, fraudulent, duplicated, treated as no-shows, or otherwise fail to meet the criteria for a Materialized Booking under the General Terms.

**3.4 Payment Flows**
a. For certain hotel or accommodation Bookings, the Agent may initiate the Booking via the Extranet, and payment may be collected directly by the Supplier from the Client. In such cases, Axonall's commission to the Agent (if any) will be based on the terms notified in the Extranet and the amounts Axonall receives from the Supplier.
b. For experiences and certain packages or products, Axonall (or its payment service providers) may act as payment facilitator and collect funds directly from the Client at the point of Booking, in accordance with the applicable booking terms and the General Terms.

**3.5 Commission Statements and Payment Timing**
a. Axonall shall make available to the Agent periodic commission statements via the Extranet (or by email) showing Materialized Bookings and the commission amounts due.
b. The Agent shall review each statement and notify Axonall in writing of any good faith dispute or discrepancy within thirty (30) days of the statement being made available. If no such notice is received within that period, the statement shall be deemed accepted and conclusive, except in the case of manifest error.
c. Subject to this Agreement and the General Terms, Axonall shall remit commission amounts due to the Agent in accordance with Axonall's standard commission payment cycle as notified from time to time via the Extranet.

**3.6 Set-Off and Deductions**
Axonall may deduct from, or set off against, any commission otherwise due to the Agent any:
a. refunds or credits paid or payable to Clients;
b. chargebacks or similar adjustments; or

c. other amounts reasonably attributable to the Agent's breach, misrepresentation, error, or failure to follow the booking rules or Supplier conditions,
to the extent such amounts arise from Bookings made by the Agent.

**3.7 Taxes and Self-Billing**
a. The Agent is responsible for any taxes (including VAT, GST or sales tax) arising from the commission it receives under this Agreement. The Agent shall provide Axonall with accurate and up-to-date billing and tax information (including any applicable tax registration number) and shall promptly notify Axonall of any changes to such information.
b. Where permitted by Applicable Law, the Parties agree to operate a self-billing arrangement in respect of commission payable to the Agent. Under this arrangement, Axonall shall generate periodic self-billed invoices or commission statements on the Agent's behalf for the commission amounts due and make these available via the Extranet. Such documents shall constitute valid tax invoices for the purposes of Applicable Law, where required.

c. The Agent agrees not to issue separate invoices to Axonall for commission amounts covered by the self-billing process, unless Axonall notifies the Agent that self-billing will no longer be used or is not permitted under Applicable Law in the Agent's jurisdiction.

d. If self-billing is not permitted under Applicable Law for a particular Agent, Axonall may require that Agent to issue its own invoices based on the commission statements provided via the Extranet, within the timeframe reasonably specified by Axonall.

**4. USE OF THE EXTRANET AND PLATFORM**
**4.1 Access Credentials**
a. Axonall shall issue the Agent with login credentials for access to the Extranet.
b. The Agent is responsible for keeping its login credentials secure and confidential and shall ensure that only authorised personnel access the Extranet.

**4.2 Permitted Use**
The Agent shall use the Extranet solely for making legitimate Bookings for its Clients. The Agent shall not make speculative, test, or fraudulent reservations, and shall not attempt to bypass, scrape or misuse the Extranet or the Axonall platform.

**4.3 Booking Information**
a. Booking details entered by the Agent through the Extranet are transmitted to the relevant Supplier and/or processed through the Axonall platform.
b. The Agent is solely responsible for ensuring that all information it enters (including Client details, dates, room types, special requests, and payment information where applicable) is complete and accurate.
c. Axonall shall not be liable for any errors, losses or issues arising from inaccurate or incomplete information provided by the Agent.

**4.4 Suspension and Termination of Access**
Axonall's rights to suspend or terminate User access under the General Terms apply in full to this Agreement. Without limitation, Axonall may suspend or terminate the Agent's access to the Extranet in the event of misuse, fraud, material breach of this Agreement or the General Terms, non-compliance with Applicable Law, or risk to Clients, Suppliers or Axonall's brand.








**4.5 Non-Circumvention of Suppliers**
a. The Agent shall not use the Extranet or any information obtained via the Axonall platform to solicit or contract directly with Suppliers for the purpose of booking substantially similar products or services for the same Client booking on terms that bypass Axonall, unless Axonall has given its prior written consent.

b. For clarity, this clause does not prevent the Agent from dealing directly with any Supplier with whom the Agent had a bona fide, pre-existing commercial relationship prior to first using the Extranet, provided that the Agent does not use the Extranet or Axonall's Confidential Information to circumvent Axonall in relation to any specific Booking made or intended to be made via the Extranet.

**5. AGENT REPRESENTATIONS AND CONDUCT**

**5.1 The Agent warrants and undertakes that it shall:**
a. act professionally, honestly and in good faith in all dealings with Clients, Axonall and Suppliers;
b. hold and maintain all licences, permits, and registrations required under Applicable Law to act as a travel agent, tour operator, or reseller in the territories in which it operates;
c. comply with all Applicable Laws relating to advertising, consumer protection, package travel, and travel agency regulation;
d. not misrepresent Axonall, the Suppliers, or the nature or characteristics of any products or services booked via the Extranet; and
e. promptly notify Axonall if it becomes aware of any actual or suspected fraudulent, abusive, or illegal activity in relation to Bookings made via the Extranet.

**6. BRAND AND MATERIALS**
a. The Agent may use Axonall's trade marks, branding and promotional materials solely:
(i) with Axonall's prior written consent; and 
(ii) in accordance with any brand guidelines or instructions issued by Axonall from time to time.
b. All rights in the Axonall brand, platform, content, and related intellectual property are and shall remain the exclusive property of Axonall or its licensors. Nothing in this Agreement transfers any ownership rights in Axonall's intellectual property to the Agent.
c. The intellectual property provisions of the General Terms (including the license granted by Users to Axonall and the restrictions on use of Axonall IP) apply in full to the Agent's use of the Extranet and the Axonall platform.

**7. TERM AND TERMINATION**
a. This Agreement commences on the Effective Date and shall continue until terminated in accordance with this clause or the General Terms.
b. Either Party may terminate this Agreement for convenience by giving thirty (30) days' written notice to the other Party.
c. Either Party may terminate this Agreement with immediate effect by written notice if the other Party:
(i) commits a material breach of this Agreement or the General Terms and, where the breach is remediable, fails to remedy it within a reasonable period after receiving written notice to do so; or
(ii) is involved in fraud, gross misconduct or any activity that creates a serious legal, financial, or reputational risk for the other Party.
d. On termination of this Agreement for any reason:
(i) the Agent's access to the Extranet may be disabled;
(ii) the Parties shall honour any existing Materialized Bookings already confirmed prior to termination in accordance with the General Terms; and
(iii) Axonall shall pay any undisputed commission properly due and owing to the Agent in respect of Materialized Bookings, in accordance with the applicable commission cycle and the General Terms.

**8. CONFIDENTIALITY AND DATA**
**8.1 Confidentiality**
The confidentiality and publicity provisions of the General Terms apply in full to this Agreement. Without limiting those provisions, the Agent shall treat as confidential all non-public information it receives from Axonall in connection with this Agreement, including commission levels, commercial terms, platform documentation, and Extranet credentials, and shall not disclose such information to any third party except as permitted under the General Terms.

**8.2 Data Protection**
The Parties' obligations in relation to data protection, privacy and security (including the treatment of Client personal data shared for fulfilment of Bookings) are set out under Data Protection and Compliance in Appendix 3 (Data Protection, Privacy and Security Requirements) of the General Terms, which are incorporated by reference and apply in full to this Agreement. For clarity, each Party acts as an independent controller in respect of any personal data it processes under or in connection with this Agreement.

**9. LIMITATION OF LIABILITY**
**9.1 Axonall's general liability and indemnity position is set out in Liability and Indemnity clause of the General Terms, which applies in full to this Agreement.**

**9.2 Without prejudice to the exclusions and non-excludable categories of liability set out in the General Terms (including, without limitation, any liability that cannot be limited under Applicable Law, third-party intellectual property claims arising from the Agent's content, and any breach of data protection and security obligations), Axonall's total aggregate liability to the Agent arising out of or in connection with this Agreement and the Agent's use of the Extranet (whether in contract, tort (including negligence), breach of statutory duty or otherwise) in any consecutive twelve (12) month period shall not exceed the total commission amounts actually paid or payable by Axonall to the Agent under this Agreement in the twelve (12) months immediately preceding the event giving rise to the claim.**

**9.3 For clarity, the types of loss excluded (including loss of profit, revenue, goodwill, and indirect or consequential loss) and the liabilities that cannot be limited (including death or personal injury caused by negligence, fraud or fraudulent misrepresentation, certain IP claims and specific data-protection breaches) are as set out in the General Terms.**

**9.4 Axonall shall not be liable for the acts, omissions, insolvency, or service failures of any third-party Supplier, save to the extent expressly required by Applicable Law or the General Terms.**

**10. GENERAL**

**10.1 Relationship of the Parties**

Nothing in this Agreement or the General Terms shall be construed as creating an employment, joint venture, or fiduciary relationship between Axonall and the Agent. Each Party is an independent contractor.

**10.2 Governing Law and Jurisdiction**

This Agreement and any dispute or claim arising out of or in connection with it (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of England and Wales, as provided in the General Terms. The courts of England and Wales shall have exclusive jurisdiction to settle any such dispute or claim.

**10.3 Contact for Legal Queries**

For legal queries related to this Agreement, the Agent may contact: legal@axonall.com.

By accepting this Agreement, the Agent acknowledges and agrees that it has received, read, and accepted the Axonall General User Terms ("General Terms") as published in the Axonall Portal, which are incorporated by reference and form part of this Agreement.`

    // Add acceptance section if agreement is already accepted
    if (isAgreementAccepted && acceptedDetails.agreementAccepted) {
      const fontName = extractFontName(acceptedDetails.signatureFontStyle);

      agreement += `\n\n**AGREEMENT ACCEPTANCE DETAILS (ALREADY ACCEPTED)**

This agreement has already been accepted by ${formData.contactPerson || profile?.name || 'Authorized Signatory'}.

**Digital Acceptance Details:**
- Status: **AGREEMENT ACCEPTED**
- Accepted by: ${formData.contactPerson || profile?.name || 'Authorized Signatory'}
- Company: ${formData.companyName || profile?.legalBusinessName || '[Your Company]'}
- Location: ${acceptedDetails.city || '[City]'}, ${formData.country || profile?.country || '[Country]'}
- IP Address: ${acceptedDetails.ipAddress || '[IP Address]'}
- Acceptance Date: ${acceptedDetails.dateOfAcceptance ? new Date(acceptedDetails.dateOfAcceptance).toLocaleDateString() : '[Date]'}
- Acceptance Time: ${acceptedDetails.dateOfAcceptance ? new Date(acceptedDetails.dateOfAcceptance).toLocaleTimeString() : '[Time]'}`

      // Add signature if available
      if (acceptedDetails.signatureImage) {
        agreement += `\n\n**SIGNATURE:** [Uploaded Signature Image on Record]`
      } else if (acceptedDetails.signatureFontStyle) {
        agreement += `\n\n**SIGNATURE:** [Generated Signature]`
      }
    } else {
      agreement += `\n\n**DIGITAL ACCEPTANCE DETAILS:**
- Accepted by: ${formData.contactPerson || 'Authorized Signatory'}
- Company: ${formData.companyName || '[Your Company]'}
- Location: ${formData.city || '[City]'}, ${formData.country || '[Country]'}
- IP Address: ${ipAddress || '[IP Address]'}
- Acceptance Date: ${new Date().toLocaleDateString()}
- Acceptance Time: ${new Date().toLocaleTimeString()}
`
    }

    return agreement
  }

  const getCurrentSignature = () => {
    if (signatureOption === 'upload' && uploadedSignatureUrl) {
      return `\n\n[Signature: ${formData.contactPerson || 'Authorized Signatory'}]\n[Uploaded Signature Image]`
    } else if (signatureOption === 'generate' && generatedSignature) {
      const selectedStyle = signatureStyles.find(style => style.name === selectedSignatureStyle)

      if (selectedStyle) {
        return `\n\nSignature: ${generatedSignature}\n${formData.contactPerson || 'Authorized Signatory'}`
      }
      return `\n\nSignature: ${generatedSignature}\n${formData.contactPerson || 'Authorized Signatory'}`
    }
    return '\n\nSignature: _________________________\nName: ___________________________'
  }

  const validateAllFields = () => {
    const errors = {
      city: validateCity(formData.city)
    };

    setFormErrors(errors);

    // Check if there are any errors
    return Object.values(errors).every(error => error === '');
  }

  const canSubmit = () => {
    const hasValidFields = formData.contactPerson &&
      formData.city &&
      formData.country &&
      formData.companyAddress &&
      formData.companyName &&
      (signatureOption === 'upload' ? uploadedSignature : generatedSignature) &&
      agreementChecks.termsAccepted &&
      agreementChecks.privacyAccepted;

    // Also validate city specifically
    const isCityValid = validateCity(formData.city) === '';

    return !isAgreementAccepted && hasValidFields && isCityValid;
  }

  const handleSubmit = async () => {
    if (!canSubmit()) {
      setMessage({ type: 'error', text: 'Please complete all required fields and accept the terms.' })
      return
    }

    setSubmitting(true)
    try {
      const agreementData = {
        contactPerson: formData.contactPerson,
        city: formData.city.trim(),
        country: formData.country,
        companyAddress: formData.companyAddress,
        companyName: formData.companyName,
        registrationNumber: formData.registrationNumber,
        ipAddress: ipAddress,
        dateOfAcceptance: new Date().toISOString(),
        signatureType: signatureOption,
        signatureData: signatureOption === 'generate' ? {
          generatedSignature,
          signatureStyle: signatureStyles.find(s => s.name === selectedSignatureStyle) ? {
            id: signatureStyles.find(s => s.name === selectedSignatureStyle)?.id,
            name: selectedSignatureStyle,
            style: signatureStyles.find(s => s.name === selectedSignatureStyle)?.style,
            fontFamily: extractFontName(signatureStyles.find(s => s.name === selectedSignatureStyle)?.style)
          } : null,
          partnerName: formData.contactPerson,
          companyName: formData.companyName
        } : null
      }

      // Call the parent's submission handler
      await onAgreementSubmit(agreementData, uploadedSignature)

      setMessage({ type: 'success', text: 'Agreement submitted successfully! Digital acceptance recorded.' })

      // Close popup after successful submission
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Error submitting agreement:', error)
      setMessage({ type: 'error', text: error.response?.data?.message || 'An error occurred while submitting the agreement. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }



  const handleDownloadPDF = async () => {
    try {
      const agreementText = getUpdatedAgreement()

      // Parse agreement text into structured sections to control page breaks
      const renderAgreementLines = (text) => {
        const lines = text.split('\n')
        const sections = []
        let currentSection = []

        lines.forEach((line) => {
          const trimmed = line.trim()
          // Detect major section headers: **1. ...**, **AXONALL...**, **AGREEMENT...**, **DIGITAL...
          const isHeader = trimmed.startsWith('**') && (
            /^\*\*\d+\./.test(trimmed) ||
            /^\*\*AGREEMENT ACCEPTANCE/.test(trimmed) ||
            /^\*\*DIGITAL ACCEPTANCE/.test(trimmed) ||
            /^\*\*AXONALL TRAVEL AGENT AGREEMENT/.test(trimmed)
          )
          if (isHeader && currentSection.length > 0) {
            sections.push(currentSection)
            currentSection = []
          }
          currentSection.push(line)
        })
        if (currentSection.length > 0) sections.push(currentSection)

        return sections.map((section) =>
          section.map((line) => {
            if (!line.trim()) return '<div style="margin:4px 0;"> </div>'
            // Bold rendering: split on ** markers
            const parts = line.split('**')
            const html = parts.map((part, i) =>
              i % 2 === 1
                ? `<strong>${part}</strong>`
                : part
            ).join('')
            return `<div style="margin:2px 0;">${html}</div>`
          }).join('')
        ).map((sectionHtml) =>
          `<div style="page-break-inside:avoid; margin-bottom:14px;">${sectionHtml}</div>`
        ).join('')
      }

      // Build signature section HTML
      let signatureHtml = ''
      if (isAgreementAccepted && acceptedDetails.agreementAccepted) {
        const sigDate = acceptedDetails.dateOfAcceptance
          ? new Date(acceptedDetails.dateOfAcceptance).toLocaleDateString()
          : '[Date]'
        const sigTime = acceptedDetails.dateOfAcceptance
          ? new Date(acceptedDetails.dateOfAcceptance).toLocaleTimeString()
          : '[Time]'

        signatureHtml = `
          <div class="acceptance-box">
            <h3>AGREEMENT ACCEPTANCE DETAILS</h3>
            <table>
              <tr><td class="label">Status:</td><td class="accepted-status">&#10003; AGREEMENT ACCEPTED</td></tr>
              <tr><td class="label">Accepted By:</td><td class="value">${formData.contactPerson || profile?.name || 'Authorized Signatory'}</td></tr>
              <tr><td class="label">Company:</td><td class="value">${formData.companyName || profile?.legalBusinessName || '[Your Company]'}</td></tr>
              <tr><td class="label">Location:</td><td class="value">${acceptedDetails.city || '[City]'}, ${formData.country || profile?.country || '[Country]'}</td></tr>
              <tr><td class="label">IP Address:</td><td class="value">${acceptedDetails.ipAddress || '[IP Address]'}</td></tr>
              <tr><td class="label">Date:</td><td class="value">${sigDate}</td></tr>
              <tr><td class="label">Time:</td><td class="value">${sigTime}</td></tr>
            </table>
            ${acceptedDetails.signatureImage
            ? `<div style="margin-top:16px;"><p class="sig-label">Signature on Record:</p><img src="${acceptedDetails.signatureImage}" class="sig-img" crossorigin="anonymous" /></div>`
            : acceptedDetails.signatureFontStyle
              ? `<div style="margin-top:16px;"><p class="sig-label">Digital Signature:</p><p class="sig-text">${formData.contactPerson || profile?.name || 'Authorized Signatory'}</p></div>`
              : ''
          }
          </div>`
      } else {
        signatureHtml = `
          <div class="acceptance-box">
            <h3>DIGITAL ACCEPTANCE DETAILS</h3>
            <table>
              <tr><td class="label">Accepted By:</td><td class="value">${formData.contactPerson || 'Authorized Signatory'}</td></tr>
              <tr><td class="label">Company:</td><td class="value">${formData.companyName || '[Your Company]'}</td></tr>
              <tr><td class="label">Location:</td><td class="value">${formData.city || '[City]'}, ${formData.country || '[Country]'}</td></tr>
              <tr><td class="label">IP Address:</td><td class="value">${ipAddress || '[IP Address]'}</td></tr>
              <tr><td class="label">Date:</td><td class="value">${new Date().toLocaleDateString()}</td></tr>
              <tr><td class="label">Time:</td><td class="value">${new Date().toLocaleTimeString()}</td></tr>
            </table>
          </div>`
      }

      // Build the full standalone HTML document for PDF generation
      const htmlContent = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11px;
    color: #1e293b;
    line-height: 1.65;
    background: #ffffff;
    padding: 0;
    margin: 0;
    width: 100%;
  }
  .section { page-break-inside: avoid; margin-bottom: 14px; }
  .line { margin: 2px 0; }
  .spacer { margin: 4px 0; }
  .acceptance-box {
    page-break-inside: avoid;
    margin-top: 28px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    background: #f8fafc;
  }
  .acceptance-box h3 {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
  }
  .acceptance-box table {
    width: 100%;
    font-size: 11px;
    border-collapse: collapse;
  }
  .acceptance-box td { padding: 4px 0; }
  .label { color: #64748b; width: 35%; }
  .value { color: #1e293b; font-weight: 600; }
  .accepted-status { color: #16a34a; font-weight: 700; }
  .sig-label { font-size: 10px; color: #64748b; margin-bottom: 6px; }
  .sig-img { max-height: 70px; border: 1px solid #cbd5e1; border-radius: 4px; background: #fff; padding: 6px; }
  .sig-text { font-size: 22px; font-style: italic; color: #1e293b; }
</style>
</head>
<body>
  ${renderAgreementLines(agreementText)}
  ${signatureHtml}
</body>
</html>`

      const opt = {
        margin: [14, 14, 14, 14],
        filename: `Axonall-Agreement-${formData.companyName || 'Partner'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          letterRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['css', 'legacy'] }
      }

      await html2pdf().set(opt).from(htmlContent, 'string').save()
      setMessage({ type: 'success', text: 'PDF downloaded successfully' })
    } catch (err) {
      console.error('PDF generation error:', err)
      setMessage({ type: 'error', text: 'Failed to generate PDF' })
    }
  }

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (uploadedSignatureUrl) {
        URL.revokeObjectURL(uploadedSignatureUrl)
      }
    }
  }, [uploadedSignatureUrl])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[120vh] overflow-hidden">
        {/* Header */}
        <div className={`p-6 ${isAgreementAccepted ? 'bg-gradient-to-r from-green-600 to-green-700' : 'bg-gradient-to-r from-slate-800 to-slate-900'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-12 h-12 ${isAgreementAccepted ? 'bg-white/20' : 'bg-white/20'} rounded-xl flex items-center justify-center`}>
                {isAgreementAccepted ? (
                  <CheckSquare className="h-6 w-6 text-white" />
                ) : (
                  <FileText className="h-6 w-6 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">AXONALL TRAVEL AGENT AGREEMENT</h2>
                <p className={isAgreementAccepted ? 'text-green-200' : 'text-slate-300'}>
                  {isAgreementAccepted ? 'Agreement already accepted ✓' : 'Review and accept the partnership agreement'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${isAgreementAccepted ? 'hover:bg-white/20' : 'hover:bg-white/20'} rounded-xl transition-colors`}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>

        {/* Acceptance Status Banner */}
        {isAgreementAccepted && (
          <div className="bg-green-50 border-b border-green-200 p-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Agreement Already Accepted</p>
                  <p className="text-sm text-green-700">
                    This agreement was accepted on {acceptedDetails.dateOfAcceptance ? new Date(acceptedDetails.dateOfAcceptance).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col lg:flex-row h-[calc(90vh-120px)] overflow-hidden">
          {/* Left Panel - Agreement */}
          <div className="lg:w-2/3 overflow-y-auto p-6">
            {message.text && (
              <div className={`mb-4 p-4 rounded-xl ${message.type === 'error'
                ? 'bg-red-50 border border-red-200 text-red-800'
                : 'bg-green-50 border border-green-200 text-green-800'
                }`}>
                <div className="flex items-center">
                  {message.type === 'error' ? (
                    <XCircle className="h-5 w-5 mr-2" />
                  ) : (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  )}
                  {message.text}
                </div>
              </div>
            )}

            <div ref={agreementRef} className="bg-white border border-slate-200 rounded-xl p-8 pdf-capture-area">
              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap typography-body text-sm text-slate-700 leading-relaxed" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {(() => {
                    const lines = getUpdatedAgreement().split('\n');
                    const sections = [];
                    let currentSection = [];

                    lines.forEach((line, index) => {
                      // Check if line is a main section header (e.g., **1. ...**) or a major bolded header
                      const isHeader = line.trim().startsWith('**') && (
                        /^\*\*\d+\./.test(line.trim()) ||
                        /^\*\*AGREEMENT ACCEPTANCE/.test(line.trim()) ||
                        /^\*\*DIGITAL ACCEPTANCE/.test(line.trim()) ||
                        /^\*\*AXONALL TRAVEL AGENT AGREEMENT/.test(line.trim())
                      );

                      if (isHeader && currentSection.length > 0) {
                        sections.push(currentSection);
                        currentSection = [];
                      }
                      currentSection.push(line);
                    });

                    if (currentSection.length > 0) {
                      sections.push(currentSection);
                    }

                    return sections.map((section, sIndex) => (
                      <div key={sIndex} className="mb-6" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                        {section.map((line, lIndex) => (
                          <div key={lIndex} className="mb-1">
                            {line.split('**').map((part, partIndex) =>
                              partIndex % 2 === 1 ? (
                                <span key={partIndex} className="font-bold">
                                  {part}
                                </span>
                              ) : (
                                part
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
                </div>

                {/* Signature & Acceptance Details Preview */}
                <div className="mt-8 p-6 border-t border-slate-200 bg-slate-50 rounded-xl" style={{ breakInside: 'avoid' }}>
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">
                    {isAgreementAccepted ? 'Acceptance Details' : 'Signature & Acceptance Preview'}
                  </h3>

                  {isAgreementAccepted ? (
                    <div className="space-y-6">
                      {/* Acceptance Status */}
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                          <div>
                            <p className="font-bold text-green-800">Agreement Accepted ✓</p>
                            <p className="text-sm text-green-700">
                              This agreement is legally binding and has been digitally accepted
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Digital Acceptance Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Accepted By</p>
                              <p className="font-semibold text-slate-900">
                                {formData.contactPerson || profile?.name || 'Authorized Signatory'}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Building className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Company</p>
                              <p className="font-semibold text-slate-900">
                                {formData.companyName || profile?.legalBusinessName || '[Your Company]'}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Location</p>
                              <p className="font-semibold text-slate-900">
                                {acceptedDetails.city || formData.city || '[City]'}, {formData.country || profile?.country || '[Country]'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <IpIcon className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">IP Address</p>
                              <p className="font-semibold text-slate-900">
                                {acceptedDetails.ipAddress || '[IP Address]'}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Accepted On</p>
                              <p className="font-semibold text-slate-900">
                                {acceptedDetails.dateOfAcceptance
                                  ? new Date(acceptedDetails.dateOfAcceptance).toLocaleDateString()
                                  : '[Date]'
                                }
                              </p>
                            </div>
                          </div>

                          {/* <div className="flex items-center space-x-2">
                            <CheckSquare className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="text-xs text-slate-500">Signature Type</p>
                              <p className="font-semibold text-slate-900">
                                {acceptedDetails.signatureImage ? 'Uploaded Signature' : 
                                 acceptedDetails.signatureFontStyle ? `Generated (${extractFontName(acceptedDetails.signatureFontStyle)})` : 
                                 'No signature on record'}
                              </p>
                            </div>
                          </div> */}
                        </div>
                      </div>

                      {/* Signature Preview */}
                      {acceptedDetails.signatureImage && (
                        <div className="mt-4 p-4 bg-white border border-slate-200 rounded-xl">
                          <p className="text-sm font-medium text-slate-700 mb-3">Signature on Record:</p>
                          <div className="flex items-center space-x-4">
                            <img
                              src={`${acceptedDetails.signatureImage}?t=${new Date().getTime()}`}
                              crossOrigin="anonymous"
                              alt="Signature"
                              className="h-20 object-contain border border-slate-300 rounded-lg bg-white p-2"
                              onError={(e) => {
                                e.target.onerror = null
                                e.target.src = 'https://via.placeholder.com/200x80?text=Signature+Not+Available'
                              }}
                            />
                            <div>
                              <p className="text-sm text-slate-600">Digitally signed signature</p>
                              <p className="text-xs text-slate-500 mt-1">
                                Uploaded and verified
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Generated Signature Preview */}
                      {acceptedDetails.signatureFontStyle && !acceptedDetails.signatureImage && (
                        <div className="mt-4 p-4 bg-white border border-slate-200 rounded-xl">
                          <p className="text-sm font-medium text-slate-700 mb-3">Signature Style:</p>
                          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-center">
                            <div className={`${signatureStyles.find(s =>
                              extractFontName(s.style) === extractFontName(acceptedDetails.signatureFontStyle)
                            )?.style || 'text-xl'}`}>
                              {formData.contactPerson || profile?.name || 'Authorized Signatory'}
                            </div>
                          </div>
                          {/* <p className="text-xs text-slate-500 mt-2 text-center">
                            {extractFontName(acceptedDetails.signatureFontStyle)} font style
                          </p> */}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Normal signature preview for new acceptance */}
                      {signatureOption === 'upload' && uploadedSignatureUrl && (
                        <div className="flex items-center space-x-4">
                          <img
                            src={uploadedSignatureUrl}
                            alt="Signature"
                            className="h-24 object-contain border-2 border-slate-300 rounded-lg bg-white p-2"
                          />
                          <div>
                            <div className="font-semibold text-slate-900 text-lg">{formData.contactPerson}</div>
                            <div className="text-sm text-slate-600">Digitally signed</div>
                            <div className="text-xs text-slate-500 mt-2">
                              IP Address: {ipAddress}
                            </div>
                          </div>
                        </div>
                      )}
                      {signatureOption === 'generate' && generatedSignature && (
                        <div className="space-y-4">
                          <div className="p-4 bg-white rounded-lg border-2 border-slate-300 text-center">
                            <div className={signatureStyles.find(s => s.name === selectedSignatureStyle)?.style || 'text-xl'}>
                              {generatedSignature}
                            </div>
                          </div>
                          <div className="text-sm text-slate-700">
                            <div className="font-bold text-slate-900">{formData.contactPerson}</div>
                            <div className="text-xs text-slate-500 mt-2">
                              Digitally signed • IP: {ipAddress} • Style: {selectedSignatureStyle}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Download PDF Button */}
            {isAgreementAccepted && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl hover:from-slate-800 hover:to-slate-900 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Agreement PDF
                </button>
              </div>)}
          </div>

          {/* Right Panel - Form (Only show if agreement not accepted) */}
          {!isAgreementAccepted ? (
            <div className="lg:w-1/3 border-l border-slate-200 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Required Details */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Required Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Contact Person Name *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pl-11 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter authorized person name"
                        />
                        <User className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        City *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 pl-11 border rounded-lg focus:ring-2 focus:border-transparent ${formErrors.city
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-slate-300 focus:ring-blue-500'
                            }`}
                          placeholder="Enter your city (letters only)"
                        />
                        <MapPin className={`h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${formErrors.city ? 'text-red-400' : 'text-slate-400'
                          }`} />
                      </div>
                      {formErrors.city && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <XCircle className="h-3 w-3 mr-1" />
                          {formErrors.city}
                        </p>
                      )}
                      {!formErrors.city && formData.city && (
                        <p className="mt-1 text-sm text-green-600 flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Valid city name
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Country *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 pl-11 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter your country"
                        />
                        <Globe className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Company Address *
                      </label>
                      <textarea
                        name="companyAddress"
                        value={formData.companyAddress}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter your company address"
                      />
                    </div>
                  </div>
                </div>

                {/* Signature Options */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Signature</h3>

                  <div className="flex space-x-2 mb-4">
                    <button
                      onClick={() => setSignatureOption('upload')}
                      className={`flex-1 py-3 px-4 rounded-xl border transition-colors ${signatureOption === 'upload'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-500'
                        : 'bg-white border-slate-300 hover:bg-slate-50 hover:border-slate-400'
                        }`}
                    >
                      Upload Signature
                    </button>
                    <button
                      onClick={() => setSignatureOption('generate')}
                      className={`flex-1 py-3 px-4 rounded-xl border transition-colors ${signatureOption === 'generate'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-500'
                        : 'bg-white border-slate-300 hover:bg-slate-50 hover:border-slate-400'
                        }`}
                    >
                      Generate Signature
                    </button>
                  </div>

                  {signatureOption === 'upload' && (
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="signature-upload"
                        />
                        <label
                          htmlFor="signature-upload"
                          className="cursor-pointer block"
                        >
                          <div className="text-slate-600">
                            <Upload className="mx-auto h-10 w-10 mb-3" />
                            <p className="font-medium">Click to upload signature</p>
                            <p className="text-xs mt-1">PNG, JPG, SVG up to 10MB</p>
                          </div>
                        </label>
                      </div>
                      {uploadedSignature && (
                        <div className="text-sm text-green-600 flex items-center p-3 bg-green-50 rounded-lg">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {uploadedSignature.name} uploaded successfully
                        </div>
                      )}
                    </div>
                  )}

                  {signatureOption === 'generate' && (
                    <div className="space-y-4">
                      <div className="text-sm text-slate-600 mb-2">
                        Select a signature style:
                      </div>
                      <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto">
                        {signatureStyles.map((style) => (
                          <button
                            key={style.id}
                            onClick={() => handleGenerateSignature(style)}
                            className={`p-4 border-2 rounded-xl text-left transition-all ${selectedSignatureStyle === style.name
                              ? 'border-blue-500 bg-blue-50 shadow-sm'
                              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                              }`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-slate-900 mb-2">
                                  {style.name}
                                </div>
                                <div className={`whitespace-pre-line ${style.style} min-h-[40px] flex items-center justify-center`}>
                                  {formData.contactPerson || formData.companyName
                                    ? style.template(formData.contactPerson || formData.companyName)
                                    : style.template('Your Name')}
                                </div>
                              </div>
                              {selectedSignatureStyle === style.name && (
                                <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0 ml-2" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Agreement Acceptance */}
                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Agreement Acceptance</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        checked={agreementChecks.termsAccepted}
                        onChange={handleCheckboxChange}
                        className="mt-1 mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                        id="terms-checkbox"
                      />
                      <label htmlFor="terms-checkbox" className="text-sm text-slate-700">
                        I have read and understood the Axonall Travel Agent Agreement, and I agree to be legally bound by its terms.
                      </label>
                    </div>
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        name="privacyAccepted"
                        checked={agreementChecks.privacyAccepted}
                        onChange={handleCheckboxChange}
                        className="mt-1 mr-3 h-5 w-5 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                        id="privacy-checkbox"
                      />
                      <label htmlFor="privacy-checkbox" className="text-sm text-slate-700">
                        I have read and accept the Axonall General User Terms.
                      </label>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <button
                    onClick={handleSubmit}
                    disabled={!canSubmit() || submitting}
                    className={`w-full py-4 rounded-xl font-medium transition-all duration-200 ${!canSubmit() || submitting
                      ? 'bg-slate-300 cursor-not-allowed text-slate-500'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                      }`}
                  >
                    {submitting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        Submitting...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Submit Agreement
                      </div>
                    )}
                  </button>
                  {!canSubmit() && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Please complete all required fields and accept the terms
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // If agreement is accepted, show readonly info
            <div className="lg:w-1/3 border-l border-slate-200 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Agreement Status */}
                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">Agreement Status</h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-green-800">Accepted ✓</p>
                        <p className="text-sm text-green-700">
                          This agreement is legally binding
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Accepted On:</span>
                        <span className="font-medium text-slate-900">
                          {acceptedDetails.dateOfAcceptance
                            ? new Date(acceptedDetails.dateOfAcceptance).toLocaleDateString()
                            : 'N/A'
                          }
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">IP Address:</span>
                        <span className="font-medium text-slate-900">
                          {acceptedDetails.ipAddress || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Location:</span>
                        <span className="font-medium text-slate-900">
                          {acceptedDetails.city || 'N/A'}
                        </span>
                      </div>
                      {/* <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Signature Type:</span>
                        <span className="font-medium text-slate-900">
                          {acceptedDetails.signatureImage ? 'Uploaded' : 
                           acceptedDetails.signatureFontStyle ? `Generated (${extractFontName(acceptedDetails.signatureFontStyle)})` : 
                           'N/A'}
                        </span>
                      </div> */}
                    </div>
                  </div>
                </div>

                {/* Close Button */}
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <button
                    onClick={onClose}
                    className="w-full py-4 rounded-xl font-medium bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    Close Agreement View
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AgreementPopup